import React from 'react';
import { EmployeeDetailsContent } from "../components/EmployeeDetailsContent";
import moment from "moment";
import { Button } from "./ui/button";
import { useState } from "react";
import { X, User } from "lucide-react";
import { getTipCommissionLabel, TIP_ROLES } from "../utils/tipCommission";

  export const EmployeeTotalsTable = (props) => {
    const {
        employeeTotals = [],
        dateRange = {},
        employeeApiService,
        employees = [],
        tipRole = TIP_ROLES.WAITRESS,
        taxLabel,
    } = props;
    const commissionLabel =
      taxLabel || getTipCommissionLabel(tipRole, dateRange?.end || new Date());
    
    const [modalRecipient, setModalRecipient] = useState(null);
    // State for employee details modal
    const [loadingEmployeeDetails, setLoadingEmployeeDetails] = useState(false);
    const [employeeDetailsError, setEmployeeDetailsError] = useState(null);
    const [showEmployeeDetailsModal, setShowEmployeeDetailsModal] = useState(false);
    const [employeeDetails, setEmployeeDetails] = useState(null);

    const handleRecipientNameClick = async (recipientName, e) => {
        e.stopPropagation();

        // Find employee by name
        const employee = employees.find(emp => emp.name === recipientName);

        if (employee && employeeApiService) {
            // Show employee details modal
            setLoadingEmployeeDetails(true);
            setEmployeeDetailsError(null);
            setShowEmployeeDetailsModal(true);

            try {
                const startDate = dateRange?.start ? moment(dateRange.start).format('YYYY-MM-DD') : moment().subtract(30, 'days').format('YYYY-MM-DD');
                const endDate = dateRange?.end ? moment(dateRange.end).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD');

                const [completeDataResponse, performanceResponse] = await Promise.all([
                    employeeApiService.getCompleteEmployeeData(employee.id, { startDate, endDate }),
                    employeeApiService.getEmployeePerformanceMetrics(employee.id, { startDate, endDate })
                ]);

                if (completeDataResponse.success && performanceResponse.success) {
                    setEmployeeDetails({
                        employee: employee,
                        completeData: completeDataResponse?.data?.body?.data,
                        performanceMetrics: performanceResponse?.data?.body?.data
                    });
                } else {
                    setEmployeeDetailsError('Failed to load employee details');
                }
            } catch (error) {
                console.error('Error loading employee details:', error);
                setEmployeeDetailsError('Error loading employee details');
            } finally {
                setLoadingEmployeeDetails(false);
            }
        } else {
            // Fallback to original recipient modal
            setModalRecipient(recipientName);
            setShowEmployeeDetailsModal(true);
        }
    };

    const closeEmployeeDetailsModal = () => {
        setShowEmployeeDetailsModal(false);
        setEmployeeDetails(null);
        setEmployeeDetailsError(null);
    };

    const formatCurrency = (value) => {
        return parseFloat(value || 0).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    if (employeeTotals.length === 0) {
      return (
        <div className="text-center py-12 text-gray-500">
          No employee data available for the selected date range
        </div>
      );
    }

    return (
        <>
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white border border-gray-200 rounded-lg">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                Employee
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                Total Tip Out
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                {commissionLabel.toUpperCase()} TOTAL
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                Total Paid Out
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {employeeTotals.map((employee, index) => (
              <tr key={index} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                   <button
                                        className="font-medium text-blue-700 hover:text-blue-900 hover:underline cursor-pointer"
                                        onClick={(e) => handleRecipientNameClick(employee.employee, e)}
                                    >
                                        {employee.employee || '-'}
                                    </button>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  ${formatCurrency(employee.tipOut)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  -${formatCurrency(employee.tax)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                  ${formatCurrency(employee.paidOut)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-gray-50">
            <tr>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                Total
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                ${formatCurrency(employeeTotals.reduce((sum, emp) => sum + emp.tipOut, 0))}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                ${formatCurrency(employeeTotals.reduce((sum, emp) => sum + emp.tax, 0))}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-green-600">
                ${formatCurrency(employeeTotals.reduce((sum, emp) => sum + emp.paidOut, 0))}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      {showEmployeeDetailsModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between p-6 border-b">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                    <User className="h-5 w-5 text-blue-600" />
                                </div>
                                <div>
                                    <h2 className="text-xl font-semibold text-gray-900">
                     {employeeDetails?.employee?.name || modalRecipient}
                     (
                      <span>{(employeeDetails?.employee?.role || '')}</span>)
                  </h2>
                                    <p className="text-sm text-gray-500">
                                        Employee Details
                                        {dateRange && dateRange.start && dateRange.end && (
                                            <span className="ml-2">
                                                ({moment(dateRange.start).format("MM/DD/YYYY")} - {moment(dateRange.end).format("MM/DD/YYYY")})
                                            </span>
                                        )}
                                    </p>
                                </div>
                            </div>
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={closeEmployeeDetailsModal}
                                className="h-8 w-8"
                            >
                                <X className="h-4 w-4" />
                            </Button>
                        </div>

                        <div className="p-6">
                            {loadingEmployeeDetails ? (
                                <div className="flex justify-center items-center py-8">
                                    <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-blue-500"></div>
                                </div>
                            ) : employeeDetailsError ? (
                                <div className="text-center py-8 text-red-600">
                                    {employeeDetailsError}
                                </div>
                            ) : employeeDetails ? (
                                <>
                                    
                                    <EmployeeDetailsContent employeeDetails={employeeDetails} />
                                
                                </>
                            ) : (
                                <div className="flex flex-col items-center justify-center py-12">
                                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                                        <User className="h-8 w-8 text-gray-400" />
                                    </div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No Details Found</h3>
                                    <p className="text-gray-500 text-center max-w-md">
                                        No employee details are available for the selected period.
                                        Please check if the employee has any recorded activity during this time.
                                    </p>
                                </div>
                            )}
                        </div>

                        <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
                            <Button onClick={closeEmployeeDetailsModal}>
                                Close
                            </Button>
                        </div>
                    </div>
                </div>
            )}
      </>
    );
  };